/**
 * TIME TRACKER — CRUD primitives shared by the Shto/Largo (add/remove) actions.
 *
 * Kept out of TimeTracking.js so the add/remove behaviour that used to silently
 * no-op is unit-testable without mounting the whole 2k-line screen:
 *   · normalizeProject  — every list keyed by project reads p.Id/p.Name, but
 *                         GET /api/projects used to answer snake_case only.
 *   · upsertById        — one insert/replace rule for all five collections.
 *   · removeById        — the matching removal (id types differ per endpoint:
 *                         the row carries a number, the click carries whatever
 *                         the render put in the dialog).
 *   · createWriteRunner — synchronous in-flight guard (React state updates are
 *                         async, so `disabled={saving}` alone cannot stop a
 *                         double click) + a single error funnel.
 */

/** Which key holds the primary id, per apiService store name. */
export const COLLECTION_ID_KEY = {
  timeEntries:    'id',
  projects:       'Id',
  tasks:          'Id',
  taskActivities: 'id',
  activityGroups: 'id',
};

/**
 * GET /api/projects answers a different shape than POST/PUT /api/projects
 * (raw DB columns vs the projFromDb mapping), and the demo localStorage mode
 * answers a third. Time Tracking groups activities/groups/entries by
 * String(p.Id), so a missing Id silently emptied whole tabs. Normalize once on
 * the way in and every downstream lookup keeps working whatever the source.
 */
export function normalizeProject(p) {
  if (!p || typeof p !== 'object') return p;
  const id = p.Id ?? p.id ?? null;
  return {
    ...p,
    Id: id,
    id,
    Name:        p.Name        ?? p.name         ?? '',
    Description: p.Description ?? p.description  ?? '',
    Status:      p.Status      ?? p.status       ?? 'Active',
    Color:       p.Color       ?? p.color        ?? '#6366f1',
    BudgetHours: p.BudgetHours ?? p.budget_hours ?? 0,
  };
}

/** Ids cross the wire as numbers and come back from inputs as strings. */
export function sameId(a, b) {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return String(a) === String(b);
}

/** Insert a created record, or replace the existing one on edit. */
export function upsertById(list, idKey, rec) {
  const arr = Array.isArray(list) ? list : [];
  if (!rec) return arr;
  const id = rec[idKey] ?? rec[idKey === 'Id' ? 'id' : 'Id'];
  const at = arr.findIndex(x => sameId(x?.[idKey] ?? x?.id ?? x?.Id, id));
  if (at === -1) return [...arr, rec];
  const next = arr.slice();
  next[at] = { ...arr[at], ...rec };
  return next;
}

/** Drop a record — the optimistic half of "Largo". */
export function removeById(list, idKey, id) {
  const arr = Array.isArray(list) ? list : [];
  return arr.filter(x => !sameId(x?.[idKey] ?? x?.id ?? x?.Id, id));
}

/**
 * Turn an apiService rejection into something a user can act on. The backend
 * gates project writes behind requireRole('admin','superadmin','menaxher'), so
 * "HTTP 403" is a permission problem, not a bug.
 */
export function crudErrorMessage(err) {
  // `err.message` even when it is '' — String(new Error('')) is the useless "Error".
  const raw = String((err && typeof err === 'object' && 'message' in err) ? err.message : (err ?? '')).trim();
  if (/\b403\b|forbidden/i.test(raw)) return 'Nuk keni leje për këtë veprim.';
  if (/\b401\b|session expired/i.test(raw)) return 'Sesioni skadoi — kyçu përsëri.';
  if (/failed to fetch|networkerror|network request failed/i.test(raw)) {
    return 'Nuk u lidh dot me serverin. Kontrollo lidhjen dhe provo përsëri.';
  }
  return raw ? `Gabim: ${raw}` : 'Gabim i panjohur.';
}

/**
 * Runs one write at a time per key.
 *
 * The guard is a plain Set rather than React state on purpose: setState is
 * async, so a second click landing in the same tick would still see
 * saving === false and fire a duplicate POST/DELETE.
 *
 * @param {(keys: string[]) => void} [setBusy] receives the current in-flight keys
 * @param {(err: Error, key: string) => void} [onError]
 * @returns {(key: string, fn: () => Promise<any>) => Promise<boolean>} true when the write ran and succeeded
 */
export function createWriteRunner({ setBusy, onError } = {}) {
  const inFlight = new Set();
  const publish = () => { if (setBusy) setBusy(Array.from(inFlight)); };

  return async function run(key, fn) {
    if (inFlight.has(key)) return false;   // duplicate click — ignore, do not re-send
    inFlight.add(key);
    publish();
    try {
      await fn();
      return true;
    } catch (err) {
      if (onError) onError(err, key);
      return false;
    } finally {
      inFlight.delete(key);
      publish();
    }
  };
}
